/** @jsx jsx */
import { jsx } from '@emotion/react';

import { DescriptionPage_CourseType as CourseType } from '__generated__/graphql-types';
import { compose } from 'recompose';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import EnterpriseCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseCourseEnrollButtonNotConfigurable';
import LoadingOverlay from 'bundles/unified-description-page-common/components/loading/LoadingOverlay';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import CoursePageSEO from 'bundles/unified-description-page-common/components/seo/CoursePageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseLearnerFlowOptimization } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import { useAutoContinueWithCourse } from 'bundles/unified-description-page-common/hooks/useContinueWithCourse';
import useEnterpriseTrackingAndNavigation from 'bundles/unified-description-page-common/hooks/useEnterpriseTrackingAndNavigation';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import useSwitchToCurrentProgram from 'bundles/unified-description-page-common/hooks/useSwitchToCurrentProgram';
import Renderer from 'bundles/unified-description-page-common/pages/enterprise/course/EnterpriseCoursePageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  COURSE_NAV_ITEMS,
  getCoursePageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getEnterpriseRouteForCourseProducts } from 'bundles/unified-description-page-common/utils/getRouteForCourseProducts';

import _t from 'i18n!nls/xdp';

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseCoursePageRootContent = ({ naptime }: Props) => {
  const pageData = useEnterpriseCoursePageData();
  const { search, query } = useLocation();
  const { resourcePath, isLoading: isLoadingAutoContinue } = useAutoContinueWithCourse(
    query.isExternal,
    pageData?.slug
  );
  const isComingFromExternalPlatforms = query.isExternal === 'true';
  const { isEnrolling, isLoadingLfo } = useEnterpriseLearnerFlowOptimization();
  const shouldRedirectToNextItem = !!resourcePath;
  const { productSlug: productSlugInUrl, programSlug } = useParams();

  useEnterpriseTrackingAndNavigation({
    isLoadingAutoContinue,
    resourcePath,
  });

  useSwitchToCurrentProgram();

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    // @ts-expect-error URL: string
    photoUrl: pageData?.photoUrl,
    id: getProductId('course', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.courseType,
  });

  if (!pageData) {
    return null;
  } else if (
    !!pageData.courseType &&
    (pageData.courseType !== CourseType.StandardCourse || productSlugInUrl !== pageData.slug)
  ) {
    redirectClientOrServer(
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      getEnterpriseRouteForCourseProducts(pageData.slug, programSlug, pageData.courseType, search)
    );
  }

  const partnersToDisplay = getPartnersForCourse(pageData.partners);

  const productTitle = getProductTitle(pageData.name);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      {
        name: ModuleNames.ProductOverview,
      },
      { name: ModuleNames.ThinBanner },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Reviews,
      },
      {
        name: ModuleNames.Recommendations,
      },
      {
        name: ModuleNames.AiDisclaimer,
      },
    ],
  };

  const shouldShowAiDubbingOnXdp = !!pageData.dubbedLanguages?.length;

  const navigationLinks = getCoursePageNavigationLinks({
    pageData,
    pageConfig,
  });

  const enrollButtonSmall = (
    <EnterpriseCourseEnrollButtonNotConfigurable
      courseType={pageData.courseType}
      productId={pageData.id}
      slug={pageData.slug}
      size="small"
      // eslint-disable-next-line no-restricted-syntax
      buttonLabel={_t('Go To Course')}
    />
  );
  // Redirection would be in progress if resourcePath is available
  // Show a loader if redirection is in progress with optimised learner flow enabled
  if (
    isComingFromExternalPlatforms &&
    (isLoadingAutoContinue || shouldRedirectToNextItem || isEnrolling || isLoadingLfo)
  ) {
    // eslint-disable-next-line no-restricted-syntax
    return <LoadingOverlay label={_t('Loading the course...')} />;
  }

  return (
    <PageInfoProvider
      programSlug={programSlug}
      navigationIdMap={COURSE_NAV_ITEMS}
      productSlug={pageData.slug}
      hasDubbedLanguages={shouldShowAiDubbingOnXdp}
    >
      <EventingDataProvider productId={pageData.id} productType="course">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
                dubbedLanguages: shouldShowAiDubbingOnXdp ? pageData.dubbedLanguages : null,
              }}
            >
              <FixedPageNavigation
                productTitle={productTitle}
                navigationLinks={navigationLinks}
                pageCta={enrollButtonSmall}
                partnerLogo={
                  partnersToDisplay && partnersToDisplay.length > 1 ? (
                    <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                  ) : (
                    <NavPartnerLogo
                      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                      imageSrc={partnersToDisplay?.[0].squareLogo}
                      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                      partnerName={partnersToDisplay?.[0].name}
                    />
                  )
                }
              />
              <Renderer pageData={pageData} pageConfig={pageConfig} naptime={naptime} />
              <MobileCtaModule pageCta={enrollButtonSmall} />
              <CoursePageSEO pageData={pageData} disableCrawlerIndexing />
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

// Need naptime instance for enterprise save functionality.
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseCoursePageRootContent);
